// ============================================
// DAY REPORT — Full page for a single day's analytics
// Route: /admin/analytics/day/:date
// ============================================
import { useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { generateDayReport } from "./mockData";
import { fmt } from "./analyticsHelpers";
import { VisitorList } from "./VisitorList";
import { DayPagesSources } from "./DayPagesSources";
import { DayHourlyBreakdown } from "./DayHourlyBreakdown";
import styles from "./DayReport.module.css";

export const DayReport = () => {
    const { date } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("visitors");

    // Generate report data from the date param
    const report = useMemo(() => {
        if (!date || !dayjs(date).isValid()) return null;
        return generateDayReport(date);
    }, [date]);

    if (!report) {
        return (
            <div className={styles.container}>
                <div className={styles.emptyState}>
                    <span style={{ fontSize: 48 }}>📅</span>
                    <h2>No data available</h2>
                    <p>The date &quot;{date}&quot; doesn&apos;t have any visitor data.</p>
                    <Link to="/admin/analytics" className={styles.backLink}>← Back to Analytics</Link>
                </div>
            </div>
        );
    }

    // Navigate to previous / next day
    const goToDay = (offset) => {
        const newDate = dayjs(date).add(offset, "day").format("YYYY-MM-DD");
        navigate(`/admin/analytics/day/${newDate}`);
    };

    return (
        <div className={styles.container}>
            {/* ===== HEADER ===== */}
            <header className={styles.header}>
                <div className={styles.headerTop}>
                    <Link to="/admin/analytics" className={styles.backBtn}>
                        <span className="material-symbols-outlined">arrow_back</span> Analytics
                    </Link>
                    <div className={styles.headerCenter}>
                        <button className={styles.dayNavBtn} onClick={() => goToDay(-1)}>
                            <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <div className={styles.headerTitle}>
                            <span className={styles.headerIcon}>📋</span>
                            <div>
                                <h1>{report.dateFormatted}</h1>
                                <p>Full daily visitor report</p>
                            </div>
                        </div>
                        <button
                            className={styles.dayNavBtn}
                            onClick={() => goToDay(1)}
                            disabled={dayjs(date).add(1, "day").isAfter(dayjs(), "day")}
                        >
                            <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                    </div>
                    <div style={{ width: 100 }} /> {/* spacer for centering */}
                </div>
            </header>

            <main className={styles.main}>
                {/* ===== SUMMARY KPI ROW ===== */}
                <div className={styles.kpiRow}>
                    {[
                        { label: "Total Visitors", value: fmt(report.summary.totalVisitors), icon: "👥" },
                        { label: "Page Views", value: fmt(report.summary.totalPageViews), icon: "📄" },
                        { label: "Avg Duration", value: report.summary.avgDurationFormatted, icon: "⏱️" },
                        { label: "Bounce Rate", value: `${report.summary.bounceRate}%`, icon: "🏀" },
                        { label: "New Users", value: fmt(report.summary.newUsers), icon: "🆕" },
                        { label: "Conversions", value: `${report.summary.conversions} (${report.summary.conversionRate}%)`, icon: "🎯" },
                    ].map((k, i) => (
                        <div key={i} className={styles.kpiCard}>
                            <span className={styles.kpiIcon}>{k.icon}</span>
                            <div>
                                <div className={styles.kpiLabel}>{k.label}</div>
                                <div className={styles.kpiValue}>{k.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ===== TAB NAVIGATION ===== */}
                <div className={styles.tabs}>
                    {[
                        { key: "visitors", label: "Visitors", icon: "group" },
                        { key: "pages", label: "Pages & Sources", icon: "description" },
                        { key: "hourly", label: "Hourly Breakdown", icon: "schedule" },
                    ].map(t => (
                        <button key={t.key}
                            className={`${styles.tab} ${activeTab === t.key ? styles.tabActive : ""}`}
                            onClick={() => setActiveTab(t.key)}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{t.icon}</span>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* ===== TAB CONTENT ===== */}
                <div className={styles.tabPanel}>
                    {activeTab === "visitors" && (
                        <VisitorList visitors={report.visitors} />
                    )}
                    {activeTab === "pages" && (
                        <DayPagesSources
                            topPages={report.topPages}
                            referrerBreakdown={report.referrerBreakdown}
                            deviceBreakdown={report.deviceBreakdown}
                        />
                    )}
                    {activeTab === "hourly" && (
                        <DayHourlyBreakdown hourly={report.hourly} />
                    )}
                </div>
            </main>
        </div>
    );
};
